import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTripDocument } from "../../../../context/TripDocumentContext";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { SendTripDocumentRequest } from "../../../../types/api";
import { PODPayloadBufferIds } from "../../docPayloads";
import { useTripContext } from "../../../../context/tripsContext";
import { useAuth } from "../../../../utils/hooks/useAuth";
import { toast } from "react-toastify";
import { docSide } from "../../style";
import { useLocation, useParams } from "react-router-dom";
import { appInsights, trackFullPayload } from "../../../../utils/appInsights";
import { useUserFlowTracking } from "../../../../utils/hooks/useUserFlowTracking";
import { useDocumentNavigationData } from "../../../../utils/hooks/useDocumentNavigationData";
import { useNavigationContext } from "../../../../context/navigationContext";
import { RelatedLoad } from "../../../../types/api";

interface ISidebarTimelineProps {
  isOpen: boolean;
  callbackCloseSidebar: (shouldFetchDocuments: boolean) => void;
  callbackUpdateDocuments?: (documents: any) => void;
  index: number;
  maxIndex?: number;
  action: boolean;
  onDocumentUploaded?: () => void;
  // Dati specifici della card selezionata
  selectedCardData?: {
    selectedLoad: RelatedLoad;
    relatedDocs: any[];
    cardIndex: number;
    tmsLocationRelatedLoad: string;
    tmsLocation: string;
    filteredDocuments: any[];
  };
}

const SidebarAddDocumentTimeline: React.FC<ISidebarTimelineProps> = ({
  isOpen = false,
  callbackCloseSidebar,
  callbackUpdateDocuments,
  index,
  action,
  onDocumentUploaded,
  // Dati specifici della card selezionata
  selectedCardData
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const params = useParams();
  
  // Estrai il tripId dal pathname
  const tripIdFromUrl = currentPath.match(/\/tripdetails\/(\d+)/)?.[1];

  const [docName, setDocName] = useState<string>("");
  const [timestamp, setTimestamp] = useState<string>("");

  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { uploadPOD, isDocumentLoading, sendTripDocument, errorMessage } =
    useTripDocument();

  const { tripDetails, stops } = useTripContext();
  const { user } = useAuth();
  const { trackDocumentUploadFlow, trackInteraction } = useUserFlowTracking();
  const { getNavigationDataAsString } = useDocumentNavigationData();
  const { navigationSteps, sessionId } = useNavigationContext();

  // Per la timeline, usiamo i dati dalla card selezionata
  const loadingOptions = selectedCardData ? [selectedCardData.selectedLoad] : [];

  // selectedOption si inizializza con tmsLocationRelatedLoad se presente
  const [selectedOption, setSelectedOption] = useState<string>(selectedCardData?.tmsLocationRelatedLoad || "");

  // Aggiorna il valore selezionato quando cambia selectedCardData o quando la sidebar si apre
  useEffect(() => {
    console.log('[DEBUG TIMELINE] useEffect triggered', { 
      isOpen, 
      selectedCardData, 
      index, 
      loadingOptionsLength: loadingOptions.length 
    });
    if (isOpen && selectedCardData) {
      // Se c'è solo un loading option disponibile, selezionalo automaticamente
      if (loadingOptions.length === 1) {
        const autoSelectedOption = loadingOptions[0].tmsLocation;
        setSelectedOption(autoSelectedOption);
        console.log('[DEBUG TIMELINE] Auto-selected single loading option:', autoSelectedOption);
      } else if (loadingOptions.length === 0) {
        // Se non ci sono loading options, svuota la selezione
        setSelectedOption("");
        console.log('[DEBUG TIMELINE] No loading options available');
      } else {
        setSelectedOption(selectedCardData.tmsLocationRelatedLoad || "");
        console.log('[DEBUG TIMELINE] setSelectedOption called with', selectedCardData.tmsLocationRelatedLoad);
      }
    }
  }, [isOpen, selectedCardData, index, loadingOptions]);

  // Funzioni helper per formattare i dati
  const formatDuration = (milliseconds: number): string => {
    if (milliseconds < 1000) return `${milliseconds}ms`;
    if (milliseconds < 60000) return `${Math.round(milliseconds / 1000)}s`;
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.round((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getPageName = (path: string): string => {
    const pathMap: { [key: string]: string } = {
      '/dashboard': 'Dashboard',
      '/services': 'Servizi',
      '/documents': 'Documenti',
      '/contracts': 'Contratti',
      '/trips': 'Viaggi',
      '/fleet-and-route': 'Flotta e Percorsi',
      '/settings': 'Impostazioni',
      '/profile': 'Profilo',
      '/trip': 'Dettagli Viaggio',
      '/trip-documents': 'Documenti Viaggio',
      '/error-page': 'Pagina Errore'
    };
    
    return pathMap[path] || path;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedOption(value);
    trackInteraction('select', 'loading_point_dropdown_timeline', 'change', {
      selectedValue: value,
      action: action
    });
    console.log('[DEBUG TIMELINE] handleDropdownChange selectedOption:', value);
  };

  const handleDropdownClick = () => {
    console.log('[DEBUG TIMELINE] Dropdown clicked');
    console.log('[DEBUG TIMELINE] Current selectedOption:', selectedOption);
    console.log('[DEBUG TIMELINE] Current selectedCardData:', selectedCardData);
  };

  // Funzione per validare il file
  const validateFile = (file: File): { isValid: boolean; errorMessage?: string } => {
    // Validazione estensione file
    const allowedExtensions = ['jpeg', 'jpe', 'jpg', 'png', 'tiff', 'tif', 'pdf'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        errorMessage: t("errors.fileValidation.unsupportedFormat", { 
          formats: allowedExtensions.join(', ').toUpperCase() 
        }) as string
      };
    }

    // Validazione dimensione file (20MB = 20 * 1024 * 1024 bytes)
    const maxSizeInBytes = 20 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return {
        isValid: false,
        errorMessage: t("errors.fileValidation.fileTooLarge", { 
          maxSize: "20MB" 
        }) as string
      };
    }

    return { isValid: true };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validazione del file
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.errorMessage || t("errors.fileValidation.invalidFile"));
        // Reset del file input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }

      const currentTimestamp =
        new Date().toISOString().replace("T", " ").split(".")[0] + ".000";
      
      setSelectedFile(file);
      setDocName(file.name);
      setTimestamp(currentTimestamp);

      // Traccia l'inizio del flusso di upload del documento
      trackDocumentUploadFlow.startDocumentUpload(file.name, file.size, file.type, {
        action: action,
        hasSelectedCardData: !!selectedCardData,
        selectedOption: selectedOption,
        tripId: tripDetails?.tmsObjectNumber,
        userEmail: user?.email,
        source: 'timeline'
      });

      // Traccia l'interazione con il file input
      trackInteraction('file_input', 'document_upload_timeline', 'file_selected', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
    }
  };

  const handleDeleteFile = () => {
    if (selectedFile) {
      // Traccia l'interazione di eliminazione file
      trackInteraction('button', 'delete_file_timeline', 'click', {
        fileName: selectedFile.name
      });
    }
    setSelectedFile(null);
  };

  const extractTransportOrderKey = (transportOrderTMSKey: string): string => {
    if (!transportOrderTMSKey || transportOrderTMSKey === 'N/A' || transportOrderTMSKey === 'undefined') {
      console.log('[DEBUG TIMELINE] extractTransportOrderKey: Invalid transportOrderTMSKey:', transportOrderTMSKey);
      return 'N/A';
    }
    const parts = transportOrderTMSKey.split("_");
    return parts[parts.length - 1];
  };

  const generateStringsForTimeline = () => {
    const results: { concatenated: string; orderKey: string }[] = [];
    
    console.log('[DEBUG TIMELINE] generateStringsForTimeline called with selectedCardData:', selectedCardData);
    console.log('[DEBUG TIMELINE] Current index:', index);
    
    if (!selectedCardData || !selectedCardData.selectedLoad) {
      console.log('[DEBUG TIMELINE] No selectedCardData provided to generateStringsForTimeline');
      return results;
    }
    
    const selectedLoad = selectedCardData.selectedLoad;
    const tmsLocation = selectedCardData.tmsLocation;
    
    console.log('[DEBUG TIMELINE] Processing selected load:', selectedLoad);
    
    const transportOrderKey = extractTransportOrderKey(
      (selectedLoad as any).transportOrderTMSKey || 'N/A'
    );
    console.log(`[DEBUG TIMELINE] Extracted transport order key:`, transportOrderKey);
    
    // Crea la combinazione per questo specifico delivery stop
    const concatenated = `${selectedLoad.tmsLocation}_${tmsLocation}`;
    console.log(`[DEBUG TIMELINE] Created concatenated string:`, concatenated);
    
    results.push({
      concatenated: concatenated,
      orderKey: transportOrderKey,
    });
    
    // Log dell'array finale concatenatedStrings
    console.log('[DEBUG TIMELINE] generateStringsForTimeline results:', results);
    return results;
  };

  const [isDragging, setIsDragging] = useState(false);

  // Temporary debug useEffect
  useEffect(() => {
    console.log('[DEBUG TIMELINE] SIDEBAR TIMELINE ANALYSIS:', {
      // Basic props
      index,
      action,
      isOpen,
      
      // SelectedCardData analysis
      selectedCardData: selectedCardData,
      hasSelectedCardData: !!selectedCardData,
      
      // SelectedCardData details
      selectedLoad: selectedCardData?.selectedLoad ? {
        tmsLocation: selectedCardData.selectedLoad.tmsLocation,
        involvedPartyName: selectedCardData.selectedLoad.involvedPartyName,
        transportOrderTMSKey: (selectedCardData.selectedLoad as any).transportOrderTMSKey
      } : null,
      
      // Related documents
      relatedDocsCount: selectedCardData?.relatedDocs?.length || 0,
      cardIndex: selectedCardData?.cardIndex
    });
  }, [selectedCardData, action, index, isOpen]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Validazione del file
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.errorMessage || t("errors.fileValidation.invalidFile"));
        return;
      }

      const currentTimestamp =
        new Date().toISOString().replace("T", " ").split(".")[0] + ".000";
      
      setSelectedFile(file);
      setDocName(file.name);
      setTimestamp(currentTimestamp);

      // Traccia l'inizio del flusso di upload del documento via drag & drop
      trackDocumentUploadFlow.startDocumentUpload(file.name, file.size, file.type, {
        action: action,
        hasSelectedCardData: !!selectedCardData,
        selectedOption: selectedOption,
        tripId: tripDetails?.tmsObjectNumber,
        userEmail: user?.email,
        uploadMethod: 'drag_drop',
        source: 'timeline'
      });

      // Traccia l'interazione con il drag & drop
      trackInteraction('drag_drop', 'document_upload_timeline', 'file_dropped', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      console.log('[DEBUG TIMELINE] Upload failed: No file selected');
      return;
    }
    if (!selectedCardData || !selectedCardData.selectedLoad) {
      console.log('[DEBUG TIMELINE] Upload failed: No selectedCardData available');
      return;
    }
    
    console.log('[DEBUG TIMELINE] Upload started with:', {
      fileName: selectedFile.name,
      selectedOption,
      action,
      index,
      selectedCardData,
      selectedLoad: selectedCardData.selectedLoad
    });
    
    // Traccia l'inizio dell'operazione di upload
    appInsights.trackEvent({
      name: 'SidebarAddDocumentTimeline_Upload_Started',
      properties: {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        action: action,
        hasSelectedCardData: !!selectedCardData,
        selectedOption: selectedOption,
        source: 'timeline'
      }
    });

    // Traccia il passaggio di processing del documento
    trackDocumentUploadFlow.trackDocumentProcessing('upload_processing_timeline', {
      fileName: selectedFile.name,
      action: action
    });
    
    try {
      const response = await uploadPOD(selectedFile);
      console.log('[DEBUG TIMELINE] UploadPOD response:', response);
      
      if (response && Array.isArray(response) && response.length > 0) {
        const tmsObjectNumber = tripDetails?.tmsObjectNumber;
        const userMail = user ? user.email : "-";
        
        // Per PKL (action = true), non serve selezionare un punto di carico
        let concatenatedStrings;
        if (action) {
          concatenatedStrings = [{ concatenated: "PKL_GENERAL", orderKey: "PKL" }];
        } else if (selectedOption) {
          concatenatedStrings = [{
            concatenated: `${selectedOption}_${selectedCardData.tmsLocation}`,
            orderKey: ((selectedCardData.selectedLoad as any).transportOrderTMSKey || 'N/A').split('_').pop() || 'N/A',
          }];
        } else {
          // Se non è selezionato nessun option, usa generateStringsForTimeline per ottenere le opzioni disponibili
          console.log('[DEBUG TIMELINE] No selectedOption, using generateStringsForTimeline');
          concatenatedStrings = generateStringsForTimeline();
          console.log('[DEBUG TIMELINE] generateStringsForTimeline result:', concatenatedStrings);
        }
        console.log('[DEBUG TIMELINE] Final concatenatedStrings:', concatenatedStrings);
        console.log('[DEBUG TIMELINE] selectedOption:', selectedOption);
        console.log('[DEBUG TIMELINE] concatenatedStrings length:', concatenatedStrings.length);
        
        const selectedStop =
          concatenatedStrings[0]
            ? concatenatedStrings[0]
            : null;
        console.log('[DEBUG TIMELINE] selectedStop:', selectedStop);
        
        // Per PKL, non serve controllare selectedStop perché è sempre presente
        if (!action && !selectedStop) {
          console.log('[DEBUG TIMELINE] Upload failed: No selectedStop found for POD');
          return;
        }

        // Ottieni i dati di navigazione
        const navigationData = getNavigationDataAsString();
        const navigationDataSize = navigationData ? navigationData.length : 0;
        console.log("navigationData", navigationData);
        
        const dynamicPayload: SendTripDocumentRequest = !action
        ? {
            ...PODPayloadBufferIds,
            document: {
              bufferIds: response,
            },
            fields: [
              ...PODPayloadBufferIds.fields.filter(
                (field) =>
                  ![
                    "TESTO227_131",
                    "TESTO246_136",
                    "DocName",
                    "Originale",
                    "TESTO265_131",
                    "DATA224_131",
                    "TESTO223_131",
                  ].includes(field.name)
              ),
              {
                value: tmsObjectNumber || 0,
                name: "TESTO227_131",
                externalId: "COD_VIAGGIO",
                className: "AdditionalFieldStringDTO",
              },
              {
                value:
                  concatenatedStrings[0]
                    .concatenated,
                name: "TESTO246_136",
                externalId: "MITTENTI_DESTINATARI",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: docName,
                name: "DocName",
                className: "SubjectFieldDTO",
              },
              {
                value: selectedFile.name,
                name: "Originale",
                className: "OriginalFieldDTO",
              },
              {
                value: selectedStop?.orderKey || "N/A",
                name: "TESTO265_131",
                externalId: "COD_ORDINE",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: timestamp,
                name: "DATA224_131",
                externalId: "DATAORAFOTO",
                className: "AdditionalFieldDateTimeDTO",
              },
              {
                value: userMail,
                name: "TESTO223_131",
                externalId: "ACCOUNT",
                className: "AdditionalFieldStringDTO",
              },
            ],
          }
        : {
          ...PODPayloadBufferIds,
          document: {
            bufferIds: response,
          },
          fields: [
            ...PODPayloadBufferIds.fields.filter(
              (field) =>
                ![
                  "TESTO227_131",
                  "TESTO246_136",
                  "DocName",
                  "Originale",
                  "TESTO265_131",
                  "DATA224_131",
                  "TESTO223_131",
                  "DocumentType",
                  "COMBO221_131",
                  "NUMERIC60_8",
                  "Stato",
                ].includes(field.name)
            ),
            {
              value: tmsObjectNumber || 0,
              name: "TESTO227_131",
              externalId: "COD_VIAGGIO",
              className: "AdditionalFieldStringDTO",
            },
            {
              value:
                concatenatedStrings[0]
                  .concatenated,
              name: "TESTO246_136",
              externalId: "MITTENTI_DESTINATARI",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: docName,
              name: "DocName",
              className: "SubjectFieldDTO",
            },
            {
              value: selectedFile.name,
              name: "Originale",
              className: "OriginalFieldDTO",
            },
            {
              value: selectedStop?.orderKey || "N/A",
              name: "TESTO265_131",
              externalId: "COD_ORDINE",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: timestamp,
              name: "DATA224_131",
              externalId: "DATAORAFOTO",
              className: "AdditionalFieldDateTimeDTO",
            },
            {
              value: userMail,
              name: "TESTO223_131",
              externalId: "ACCOUNT",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: 357,
              name: "DocumentType",
              className: "DocumentTypeFieldDTO",
            },
            {
              value: "PKL",
              name: "COMBO221_131",
              externalId: "TIPO_DOCUMENTO",
              className: "AdditionalFieldComboDTO",
            },
            {
              value: "Valid",
              name: "Stato",
              className: "StateFieldDTO",
            },
          ],
        };

        // Traccia il passaggio di invio del documento
        trackDocumentUploadFlow.trackDocumentProcessing('document_sending_timeline', {
          fileName: selectedFile.name,
          action: action,
          payloadSize: JSON.stringify(dynamicPayload).length
        });

        // Crea l'oggetto JSON con tutti i dati di navigazione visibili nel FAB
        const navigationInsightData = {
          sessione: sessionId,
          utente: userMail,
          passiTotali: navigationSteps.length,
          passi: navigationSteps.map((step: any, index: number) => ({
            numero: index + 1,
            nomePagina: getPageName(step.path),
            timestamp: formatTime(step.timestamp),
            path: step.path,
            parametri: step.searchParams || null,
            hash: step.hash || null,
            durataDalPassoPrecedente: step.duration ? formatDuration(step.duration) : null,
            tempoSullaPagina: step.timeOnPage && step.timeOnPage > 0 ? formatDuration(step.timeOnPage) : null,
            provenienza: step.referrer || null
          })),
          statistiche: {
            durataTotaleSessione: navigationSteps.length > 1 ? 
              formatDuration(navigationSteps[navigationSteps.length - 1].timestamp.getTime() - navigationSteps[0].timestamp.getTime()) : 
              '0s',
            pagineUniche: Array.from(new Set(navigationSteps.map((step: any) => step.path))).length
          }
        };

        // Insight per tracciare il payload completo della chiamata
        appInsights.trackEvent({
          name: 'SidebarAddDocumentTimeline_SendTripDocument_Payload',
          properties: {
            fileName: selectedFile.name,
            action: action,
            tmsObjectNumber: tmsObjectNumber,
            userEmail: userMail,
            payload: JSON.stringify(dynamicPayload),
            timestamp: timestamp,
            docName: docName,
            concatenatedString: concatenatedStrings[0].concatenated,
            navigationInsightData: JSON.stringify(navigationInsightData, null, 2),
            tripDetailsResponse: tripDetails,
            selectedCardData: selectedCardData,
            tripId: tripIdFromUrl,
            source: 'timeline'
          }
        });

        console.log('[DEBUG TIMELINE] Final payload:', dynamicPayload);
        console.log('[DEBUG TIMELINE] About to call sendTripDocument...');
        
        try {
          const sendTripResponse = await sendTripDocument(dynamicPayload);
          console.log('[DEBUG TIMELINE] sendTripDocument call completed successfully:', sendTripResponse);
        } catch (sendError) {
          console.error('[DEBUG TIMELINE] sendTripDocument call failed:', sendError);
          throw sendError;
        }

        // Chiama la callback per fare il fetch dei documenti aggiornati
        onDocumentUploaded?.();

        // Traccia il completamento del flusso di upload
        trackDocumentUploadFlow.completeDocumentUpload(undefined, undefined, {
          fileName: selectedFile.name,
          action: action,
          tripId: tmsObjectNumber,
          userEmail: userMail,
          success: true,
          source: 'timeline'
        });

        toast.success("Documento inviato con successo");
      } else {
        // Traccia l'errore dell'upload
        appInsights.trackEvent({
          name: 'SidebarAddDocumentTimeline_Upload_Error',
          properties: {
            fileName: selectedFile.name,
            errorType: 'InvalidResponse',
            response: response,
            source: 'timeline'
          }
        });

        // Traccia il fallimento del flusso di upload
        trackDocumentUploadFlow.failDocumentUpload('InvalidResponse', {
          fileName: selectedFile.name,
          action: action,
          response: response,
          source: 'timeline'
        });
        
        toast.error("Errore durante l'invio del documento");
      }
    } catch (error) {
      // Traccia l'errore generale
      appInsights.trackEvent({
        name: 'SidebarAddDocumentTimeline_Upload_Exception',
        properties: {
          fileName: selectedFile.name,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          action: action,
          source: 'timeline'
        }
      });

      // Traccia il fallimento del flusso di upload
      trackDocumentUploadFlow.failDocumentUpload(
        error instanceof Error ? error.message : 'Unknown error',
        {
          fileName: selectedFile.name,
          action: action,
          source: 'timeline'
        }
      );
      
      toast.error("Errore durante il caricamento del file");
    } finally {
      callbackCloseSidebar(true);
      setSelectedFile(null);
    }
  };

  const onButtonClick = () => {
    inputRef.current?.click();
    
    // Traccia l'interazione con il pulsante di upload
    trackInteraction('button', 'upload_button_timeline', 'click', {
      action: action
    });
  };

  console.log('[DEBUG TIMELINE] SidebarAddDocumentTimeline props:', { 
    isOpen, 
    selectedCardData: selectedCardData,
    hasSelectedCardData: !!selectedCardData
  });
  
  return (
    <aside
      className={`flex flex-col transition z-[9999] fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar ${
        isOpen ? "translate-x-0 sidebar-shadow" : "translate-x-[100%]"
      }`}
    >
      {/* Header */}
      <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
        <div className="text-lg font-bold">
          {t("common.sidebars.addContractDocument.heading")}
        </div>
        <div
          onClick={() => callbackCloseSidebar(false)}
          className="cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-8 h-8"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="grow overflow-auto px-[30px] py-8">
        <input
          ref={inputRef}
          type="file"
          id="input-file-upload-timeline"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {!selectedFile && (
          <>
            <Box sx={docSide.box}>
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
             
                  width:'100%',
                }}
                className={`flex justify-center  ${
                  isDragging ? "border-dashed border-2 border-blue-500 rounded-lg m-1" : ""
                }`}
              >
   
    <button onClick={onButtonClick} className="upload-button" style={{ padding: "68px 12px",}}>
                  {t("common.sidebars.addContractDocument.dragNDropText")}
                </button>
              </div>
            </Box>
            {currentPath.includes("/tripdetails") &&
            !currentPath.includes("/documents") && !action ? (
              ""
            ) : !action ? (
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <Typography
                  id="select-loading-point-label-timeline"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "primary.main",
                    marginTop: "32px",
                    marginBottom: "4px",
                    textTransform: "uppercase",
                  }}
                >
                  {t("screens.trips.timeline.loadingPoint")}:
                </Typography>
                <select
                  id="loading-point-select-timeline"
                  value={selectedOption}
                  onChange={handleDropdownChange}
                  disabled={loadingOptions.length === 1}
                  style={{ 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ccc',
                    backgroundColor: loadingOptions.length === 1 ? '#f5f5f5' : 'white',
                    color: loadingOptions.length === 1 ? '#666' : 'inherit'
                  }}
                >
                  <option value="">{t('screens.trips.timeline.selectLoadingPoint')}</option>
                  {loadingOptions.map((load) => (
                    <option key={load.tmsLocation} value={load.tmsLocation}>
                      {load.involvedPartyName}
                    </option>
                  ))}
                </select>

                {loadingOptions.length === 0 && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "warning.main",
                      marginTop: "4px",
                      fontStyle: "italic"
                    }}
                  >
                    {t('screens.trips.timeline.noLoadingPoints') || "Nessun punto di carico disponibile"}
                  </Typography>
                )}
              </FormControl>
            ) : null}
          </>
        )}

        {selectedFile && (
          <Box sx={docSide.box1}>
            <Typography sx={docSide.title}>
              {t("screens.trips.tripDocuments.document")}:
            </Typography>
            <Box sx={docSide.box2}>
              <div className="mr-2">{selectedFile.name}</div>
              <div
                onClick={handleDeleteFile}
                className="cursor-pointer"
                title="Delete"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 7H5V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V7H6ZM10 19H8V10H10V19ZM16 19H14V10H16V19ZM16.618 4L15 2H9L7.382 4H3V6H21V6H16.618Z"
                    className="fill-red"
                  />
                </svg>
              </div>
            </Box>
            <Box sx={docSide.box3}>
              <Typography sx={docSide.description}>
                {t("screens.trips.tripDocuments.description")}
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
              />
            </Box>
          </Box>
        )}
      </div>
      <Box sx={docSide.box4}></Box>
      <div className="shrink-0 py-6 px-[30px] text-right border-t">
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isDocumentLoading}
          sx={docSide.button}
        >
          {t("common.sidebars.saveButtonText")}
        </Button>
      </div>
    </aside>
  );
};

export default SidebarAddDocumentTimeline; 